// Admin AI Settings — M5.4.4 (2026-04-23), sửa M23 (2026-04-24)
// 2 tabs: Providers · Routing. (Tab Embedding ĐÃ BỎ — xem dưới.)
// PRD §7.5 (Admin AI Settings).
//
// Tab 1 — Providers (openai / anthropic / deepseek / gemini):
//   - Card per provider: status badge (enabled/disabled), has_key indicator, last_test.
//   - Edit modal: api_key (password), enabled toggle, base_url, pricing, budget.
//   - Test button: POST /admin/ai/providers/:p/test → cập nhật last_test_at/ok.
//
// Tab 2 — Routing (5 tasks: ask_ai / chat_ai / grade_exam / generate_questions / embedding):
//   - Table editable inline: primary provider+model, fallback provider+model, timeout_ms, max_tokens.
//   - Save per row → PUT /admin/ai/routing/:task.
//
// (ĐÃ BỎ) Tab 3 — Embedding (M23, 2026-04-24):
//   Workflow cũ Backfill/Rebuild ALL ĐÃ GỠ. Thay bằng auto-embed khi
//   create/update/import câu hỏi. Nếu auto-embed fail, badge "⚠️ N câu chưa
//   embed" hiện ở trang QuestionsPage (header) + nút retry.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../lib/api.js';
// M23 (2026-04-24): bỏ AdminTotpReverifyModal — không còn Rebuild ALL embedding.
// M78c (2026-04-25): import lại — backend yêu cầu TOTP cho rotate api_key.
import AdminTotpReverifyModal from '../components/AdminTotpReverifyModal.jsx';

const PROVIDERS = [
  { key: 'openai',    label: 'OpenAI',    hint: 'text-embedding-3-small · gpt-4o-mini' },
  { key: 'anthropic', label: 'Anthropic', hint: 'claude-haiku · claude-sonnet' },
  { key: 'deepseek',  label: 'DeepSeek',  hint: 'deepseek-chat · OpenAI-compat' },
  { key: 'gemini',    label: 'Gemini',    hint: 'gemini-2.5-flash · gemini-2.5-pro' },
];

const TASKS = [
  { key: 'ask_ai',             label: 'Ask AI (Step 2 RAG)',    hint: 'NV hỏi AI — fallback khi không có sẵn' },
  { key: 'chat_ai',            label: 'Chat AI',                hint: 'Hội thoại đa lượt (M9+)' },
  { key: 'grade_exam',         label: 'Chấm Kiểm tra + Nội quy', hint: 'AI chấm bài thi 3 câu + Q&A module nội quy (cùng task)' },
  { key: 'generate_questions', label: 'Sinh câu hỏi',           hint: 'Từ tài liệu → sinh Q&A (M10)' },
  { key: 'embedding',          label: 'Embedding (vector)',     hint: 'Tra cứu câu hỏi có sẵn' },
];

export default function AdminAiSettingsPage() {
  const [tab, setTab] = useState('providers');

  return (
    <div className="min-h-full p-4 md:p-6">
      <div className="max-w-5xl mx-auto">
        <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-700">← Trang admin</Link>
        <div className="mt-2">
          <h1 className="text-2xl font-semibold text-brand-700">Cấu hình AI</h1>
          <p className="text-xs text-slate-500 mt-1">
            Cấu hình provider (OpenAI, Anthropic, DeepSeek, Gemini), định tuyến task → model,
            và quản lý embedding cho tính năng "Hỏi đáp".
          </p>
        </div>

        <div className="mt-4 flex gap-1 border-b border-slate-200">
          <TabButton active={tab === 'providers'} onClick={() => setTab('providers')}>Providers</TabButton>
          <TabButton active={tab === 'routing'}   onClick={() => setTab('routing')}>Routing</TabButton>
          {/* M23 (2026-04-24): Bỏ tab Embedding — chuyển sang auto-embed khi
              create/update/import. Badge "⚠️ N câu chưa embed" nằm ở trang
              Quản lý câu hỏi (QuestionsPage) khi có fail. */}
        </div>

        <div className="mt-5">
          {tab === 'providers' && <ProvidersTab />}
          {tab === 'routing'   && <RoutingTab />}
        </div>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={
        'px-4 py-2 text-sm font-medium rounded-t-lg transition ' +
        (active
          ? 'bg-brand-50 text-brand-700 border-x border-t border-slate-200'
          : 'text-slate-500 hover:text-slate-700')
      }
    >
      {children}
    </button>
  );
}

// ============================================================================
// M44 — RebuildAllEmbeddingBanner
// Xuất hiện khi có row embedding dùng model cũ (sau khi admin đổi routing).
// Bấm "Rebuild toàn app" → yêu cầu TOTP admin → invalidate + re-embed tất cả.
// ============================================================================
function RebuildAllEmbeddingBanner() {
  const [status, setStatus] = useState(null); // { current:{provider,model}, by_table:{...}, total_mismatch }
  const [loading, setLoading] = useState(true);
  const [totpOpen, setTotpOpen] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const r = await api.adminEmbedRebuildAllStatus();
      setStatus(r);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function onSubmitTotp() {
    if (busy) return;
    if (!/^\d{6}$/.test(totpCode.trim())) {
      toast.error('Mã TOTP phải là 6 chữ số');
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const r = await api.adminEmbedRebuildAll(totpCode.trim());
      setResult(r);
      toast.success(
        `✅ Đã rebuild ${r.summary?.total_embedded || 0} row ` +
        `(invalidated ${r.invalidation?.total_invalidated || 0}). ` +
        (r.summary?.total_failed > 0 ? `⚠ ${r.summary.total_failed} lỗi.` : '')
      );
      setTotpOpen(false);
      setTotpCode('');
      await load();
    } catch (e) {
      const code = e?.data?.code;
      if (code === 'ADMIN_TOTP_INVALID') {
        toast.error('Mã TOTP không đúng — nhập lại');
        setTotpCode('');
        return;
      }
      toast.error('Rebuild lỗi: ' + (e?.data?.error || e?.message));
      setTotpOpen(false);
    } finally {
      setBusy(false);
    }
  }

  if (loading) return null;
  if (!status) return null;
  const hasMismatch = (status.total_mismatch || 0) > 0;
  if (!hasMismatch && !result) return null;  // ẩn khi không có gì và chưa rebuild lần nào

  if (!hasMismatch) {
    // Đã rebuild xong — hiện kết quả ngắn
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs text-emerald-800">
        ✅ Tất cả vectors đã được embed bằng model hiện tại
        <span className="ml-1 text-emerald-600 font-medium">
          ({status.current?.provider}/{status.current?.model})
        </span>
      </div>
    );
  }

  const byTable = status.by_table || {};
  const tableBadges = [
    { key: 'questions',            label: 'Câu hỏi' },
    { key: 'rules_modules',        label: 'Chương nội quy' },
    { key: 'rules_test_questions', label: 'Câu hỏi nội quy' },
    { key: 'knowledge_chunks',     label: 'Knowledge Base' },
    { key: 'prompts',              label: 'Dặn dò AI' },
    { key: 'rules_chunks',         label: 'Nội quy (legacy)' },
  ].filter(t => (byTable[t.key] || 0) > 0);

  return (
    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl p-4">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="text-3xl">⚠️</div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-amber-900">
            Có <b className="text-amber-700">{status.total_mismatch}</b> vectors được embed bằng model cũ
          </div>
          <div className="text-xs text-amber-800/90 mt-1 leading-relaxed">
            Routing hiện tại: <b>{status.current?.provider}/{status.current?.model}</b>.
            Các vectors cũ không tương thích → tính năng Hỏi đáp / Chat AI có thể kém chính xác.
            Bấm <b>Rebuild toàn app</b> để embed lại bằng model hiện tại.
          </div>
          {tableBadges.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {tableBadges.map(t => (
                <span key={t.key} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white border border-amber-200 text-[11px] text-amber-800">
                  {t.label}: <b>{byTable[t.key]}</b>
                </span>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={() => { setTotpOpen(true); setTotpCode(''); }}
          disabled={busy}
          className="shrink-0 inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-md hover:shadow-lg disabled:opacity-60 transition-all"
        >
          {busy ? '⏳ Đang rebuild…' : '🔥 Rebuild toàn app'}
        </button>
      </div>

      {/* TOTP modal */}
      {totpOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => !busy && setTotpOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-slate-800 mb-1">🔐 Xác minh TOTP admin</h3>
            <p className="text-xs text-slate-600 mb-3">
              Đây là hành động <b>tốn chi phí</b> (~vài nghìn VND) và mất vài phút.
              Nhập 6 chữ số TOTP từ Google Authenticator / Authy để xác nhận.
            </p>
            <input
              type="text"
              value={totpCode}
              onChange={e => {
                const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                setTotpCode(v);
                if (v.length === 6) setTimeout(() => onSubmitTotp(), 100);  // auto-submit
              }}
              placeholder="••••••"
              maxLength={6}
              inputMode="numeric"
              autoFocus
              disabled={busy}
              className="w-full text-center text-2xl font-mono tracking-[0.5em] py-3 rounded-lg border-2 border-slate-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none disabled:bg-slate-50"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setTotpOpen(false)}
                disabled={busy}
                className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100 disabled:opacity-50"
              >
                Huỷ
              </button>
              <button
                onClick={onSubmitTotp}
                disabled={busy || totpCode.length !== 6}
                className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold disabled:opacity-50"
              >
                {busy ? 'Đang rebuild…' : 'Xác nhận rebuild'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Result detail nhỏ sau khi rebuild xong */}
      {result && (
        <div className="mt-3 pt-3 border-t border-amber-200 text-[11px] text-amber-800 space-y-0.5">
          <div>✅ Embedded: <b>{result.summary?.total_embedded || 0}</b> row</div>
          <div>⚠ Failed: <b>{result.summary?.total_failed || 0}</b> — retry bằng cách bấm lại</div>
          <div>💰 Cost: <b>{(result.summary?.cost_vnd || 0).toLocaleString('vi-VN')}đ</b></div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Tab 1 — Providers
// ============================================================================
function ProvidersTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);     // { provider, ...row }
  const [testing, setTesting] = useState(null);     // provider key đang test
  const [refreshing, setRefreshing] = useState(null); // M19 — provider đang refresh models

  async function reload() {
    setLoading(true);
    try {
      const { providers } = await api.adminAiProviders();
      setRows(providers);
    } catch (err) {
      toast.error(err.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { reload(); }, []);

  async function onTest(provider) {
    setTesting(provider);
    try {
      const res = await api.adminAiTestProvider(provider);
      if (res.ok) {
        toast.success(`${provider}: ${res.detail}`);
      } else {
        toast.error(`${provider}: ${res.detail || 'Test thất bại'}`);
      }
      reload();  // refresh last_test_at/ok
    } catch (err) {
      toast.error(err.data?.error || err.message);
    } finally {
      setTesting(null);
    }
  }

  // M19 (2026-04-24) — refresh list models
  async function onRefreshModels(provider) {
    setRefreshing(provider);
    try {
      const res = await api.adminAiRefreshModels(provider);
      toast.success(`${provider}: cập nhật ${res.count} models`);
      await reload();
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Refresh thất bại', { duration: 6000 });
    } finally {
      setRefreshing(null);
    }
  }

  const byProvider = Object.fromEntries(rows.map(r => [r.provider, r]));

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {PROVIDERS.map(p => {
          const row = byProvider[p.key];
          return (
            <div key={p.key} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold text-slate-800">{p.label}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{p.hint}</div>
                </div>
                {row && (
                  <div className="flex flex-col items-end gap-1">
                    <span className={
                      'text-xs px-2 py-0.5 rounded ' +
                      (row.enabled
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-slate-200 text-slate-600')
                    }>
                      {row.enabled ? 'Đang bật' : 'Tắt'}
                    </span>
                    <span className={
                      'text-xs px-2 py-0.5 rounded ' +
                      (row.has_key
                        ? 'bg-brand-100 text-brand-700'
                        : 'bg-amber-100 text-amber-700')
                    }>
                      {row.has_key ? 'Đã có key' : 'Chưa có key'}
                    </span>
                  </div>
                )}
              </div>

              {row ? (
                <div className="mt-3 space-y-1 text-xs text-slate-600">
                  <div>
                    <span className="text-slate-400">Base URL:</span>{' '}
                    <span className="font-mono">{row.base_url || '(mặc định)'}</span>
                  </div>
                  {/* M96: gỡ display giá per-provider — quản lý tại /admin/ai-pricing */}
                  <div>
                    <span className="text-slate-400">Ngân sách:</span>{' '}
                    ${row.daily_budget_usd}/ngày · ${row.monthly_budget_usd}/tháng
                  </div>
                  <div>
                    <span className="text-slate-400">Test gần nhất:</span>{' '}
                    {row.last_test_at
                      ? <span className={row.last_test_ok ? 'text-emerald-700' : 'text-rose-700'}>
                          {row.last_test_ok ? '✓ OK' : '✗ Lỗi'} · {new Date(row.last_test_at).toLocaleString('vi-VN')}
                        </span>
                      : <span className="text-slate-400">chưa test</span>
                    }
                  </div>
                  <div>
                    <span className="text-slate-400">Models:</span>{' '}
                    {row.available_models?.length > 0
                      ? <span className="text-emerald-700">✓ {row.available_models.length} models · {row.models_updated_at ? new Date(row.models_updated_at).toLocaleString('vi-VN') : '—'}</span>
                      : <span className="text-amber-700">chưa cập nhật</span>
                    }
                  </div>
                </div>
              ) : loading ? (
                <div className="mt-3 text-xs text-slate-400">Đang tải…</div>
              ) : (
                <div className="mt-3 text-xs text-slate-400">Chưa có dữ liệu</div>
              )}

              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => setEditing(row || { provider: p.key, enabled: false, has_key: false, base_url: '', daily_budget_usd: 2, monthly_budget_usd: 50 })}
                  className="text-xs px-3 py-1.5 rounded bg-brand-600 text-white hover:bg-brand-700"
                >
                  {row?.has_key ? 'Sửa' : 'Nhập key'}
                </button>
                <button
                  onClick={() => onTest(p.key)}
                  disabled={!row?.has_key || testing === p.key}
                  title={!row?.has_key ? 'Nhập key trước khi test' : ''}
                  className="text-xs px-3 py-1.5 rounded bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {testing === p.key ? 'Đang test…' : 'Test kết nối'}
                </button>
                <button
                  onClick={() => onRefreshModels(p.key)}
                  disabled={!row?.has_key || refreshing === p.key || p.key === 'gemini'}
                  title={p.key === 'gemini' ? 'Gemini chưa hỗ trợ (phase 2)' : !row?.has_key ? 'Nhập key trước' : 'Tải list model mới nhất từ API provider'}
                  className="text-xs px-3 py-1.5 rounded bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {refreshing === p.key ? '⏳ Đang tải…' : '🔄 Cập nhật models'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <ProviderEditModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </>
  );
}

function ProviderEditModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    api_key: '',              // rỗng = không đổi; '' sau khi nhập rồi xoá = user muốn xoá → xử lý riêng
    clear_key: false,         // checkbox xoá key (mutually exclusive với nhập key mới)
    enabled: !!initial.enabled,
    base_url: initial.base_url || '',
    // M97 (2026-04-26): gỡ price_per_1k_in/out — quản lý tại /admin/ai-pricing
    daily_budget_usd:   Number(initial.daily_budget_usd   ?? 2),
    monthly_budget_usd: Number(initial.monthly_budget_usd ?? 50),
  });
  const [busy, setBusy] = useState(false);
  // M78c (2026-04-25): TOTP modal state — mở khi backend trả ADMIN_TOTP_REQUIRED
  // (thường khi đổi api_key). Lưu pendingPatch để retry sau khi user nhập TOTP.
  const [totpModalOpen, setTotpModalOpen] = useState(false);
  const [pendingPatch, setPendingPatch] = useState(null);

  function buildPatch() {
    const patch = {};
    if (form.clear_key) {
      patch.api_key = '';                               // backend: '' → xoá
    } else if (form.api_key.trim()) {
      patch.api_key = form.api_key.trim();
    }
    if (form.enabled !== !!initial.enabled) patch.enabled = form.enabled;
    const nextBaseUrl = form.base_url.trim() || null;
    if (nextBaseUrl !== (initial.base_url || null)) patch.base_url = nextBaseUrl;
    // M97: bỏ patch price_per_1k_in/out — không còn UI input.
    if (form.daily_budget_usd   !== Number(initial.daily_budget_usd   ?? 2))  patch.daily_budget_usd   = form.daily_budget_usd;
    if (form.monthly_budget_usd !== Number(initial.monthly_budget_usd ?? 50)) patch.monthly_budget_usd = form.monthly_budget_usd;
    return patch;
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const patch = buildPatch();
      if (Object.keys(patch).length === 0) {
        toast('Không có thay đổi');
        onClose();
        return;
      }

      try {
        await api.adminAiUpdateProvider(initial.provider, patch);
        toast.success('Đã lưu');
        onSaved();
      } catch (err) {
        // M78c: backend yêu cầu TOTP khi đụng api_key → mở modal
        if (err.data?.code === 'ADMIN_TOTP_REQUIRED') {
          setPendingPatch(patch);
          setTotpModalOpen(true);
          return; // user nhập TOTP rồi modal sẽ retry
        }
        if (err.data?.code === 'ADMIN_TOTP_NOT_SETUP') {
          toast.error('Admin chưa setup TOTP L1 — vào Bảo mật để setup trước');
          return;
        }
        throw err;
      }
    } catch (err) {
      toast.error(err.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  }

  // M78c: callback khi user submit TOTP trong modal → retry update với adminTotp
  async function handleTotpSubmit(code) {
    // Modal sẽ catch error → setBadCode nếu invalid; throw để modal handle
    await api.adminAiUpdateProvider(initial.provider, pendingPatch, { adminTotp: code });
    toast.success('Đã lưu');
    setTotpModalOpen(false);
    setPendingPatch(null);
    onSaved();
  }

  const setF = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked :
              e.target.type === 'number'   ? Number(e.target.value) :
              e.target.value;
    setForm(f => ({ ...f, [k]: v }));
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-20 p-4">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 space-y-3 max-h-[90vh] overflow-y-auto">
        <h3 className="font-semibold text-lg">
          Cấu hình <span className="font-mono text-brand-700">{initial.provider}</span>
        </h3>

        <label className="block">
          <span className="block text-sm font-medium text-slate-700">
            API Key {initial.has_key && <span className="text-xs text-slate-400">(để trống = giữ nguyên key hiện tại)</span>}
          </span>
          <input
            type="password"
            value={form.api_key}
            onChange={setF('api_key')}
            disabled={form.clear_key}
            placeholder={initial.has_key ? '********' : 'sk-... / sk-ant-... / AIza... …'}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 font-mono text-sm disabled:bg-slate-50 disabled:text-slate-400"
            autoComplete="off"
          />
          {initial.has_key && (
            <label className="mt-2 flex items-center gap-2 text-xs text-rose-700">
              <input type="checkbox" checked={form.clear_key} onChange={setF('clear_key')} />
              Xoá key hiện tại (provider sẽ bị tự động tắt)
            </label>
          )}
        </label>

        <label className="flex items-center gap-2">
          <input type="checkbox" checked={form.enabled} onChange={setF('enabled')} />
          <span className="text-sm font-medium text-slate-700">Bật provider này</span>
        </label>

        <label className="block">
          <span className="block text-sm font-medium text-slate-700">
            Base URL <span className="text-xs text-slate-400">(để trống = mặc định của provider)</span>
          </span>
          <input
            type="url"
            value={form.base_url}
            onChange={setF('base_url')}
            placeholder="https://api.openai.com/v1"
            className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 font-mono text-sm"
          />
        </label>

        {/* M96 (2026-04-26): gỡ 2 ô giá token IN/OUT khỏi đây.
            Sau M94 lookup pricing theo (provider, model) trong bảng ai_model_pricing
            → giá ở Provider chỉ còn fallback rất hiếm khi không match model.
            Quản lý giá thực tại /admin/ai-pricing. */}
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900">
            💡 Giá API model giờ quản lý tại <Link to="/admin/ai-pricing" className="underline font-semibold">Bảng giá AI</Link> (per provider+model, có sync online từ LiteLLM).
          </div>
          <label className="block">
            <span className="block text-xs font-medium text-slate-700">Ngân sách $/ngày</span>
            <input
              type="number" step="0.01" min="0"
              value={form.daily_budget_usd} onChange={setF('daily_budget_usd')}
              className="mt-1 w-full px-2 py-1.5 rounded border border-slate-300 text-sm"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-700">Ngân sách $/tháng</span>
            <input
              type="number" step="0.01" min="0"
              value={form.monthly_budget_usd} onChange={setF('monthly_budget_usd')}
              className="mt-1 w-full px-2 py-1.5 rounded border border-slate-300 text-sm"
            />
          </label>
        </div>

        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm font-medium">Huỷ</button>
          <button type="submit" disabled={busy} className="flex-1 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium disabled:opacity-50">
            {busy ? 'Đang lưu…' : 'Lưu'}
          </button>
        </div>
      </form>

      {/* M78c (2026-04-25): TOTP modal — popup khi backend trả ADMIN_TOTP_REQUIRED */}
      <AdminTotpReverifyModal
        open={totpModalOpen}
        title={`🔐 Xác minh TOTP để đổi API key ${initial.provider}`}
        description="Đổi API key là thao tác nhạy cảm. Nhập mã 6 số từ Authenticator của admin để xác nhận."
        onClose={() => { setTotpModalOpen(false); setPendingPatch(null); }}
        onSubmit={handleTotpSubmit}
      />
    </div>
  );
}

// ============================================================================
// Tab 2 — Routing (5 task rows, save per row)
// ============================================================================
function RoutingTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingTask, setSavingTask] = useState(null);
  const [drafts, setDrafts] = useState({});  // key=task → draft object
  // M19 — models per provider (cho datalist)
  const [modelsByProvider, setModelsByProvider] = useState({});

  async function reload() {
    setLoading(true);
    try {
      const [routingRes, providersRes] = await Promise.all([
        api.adminAiRouting(),
        api.adminAiProviders(),
      ]);
      const { routing } = routingRes;
      setRows(routing);
      setDrafts({}); // clear trước khi init lại bên dưới
      // M19: lookup map provider → list model ids
      const mbp = {};
      for (const p of providersRes.providers || []) {
        mbp[p.provider] = (p.available_models || []).map(m => m.id).filter(Boolean);
      }
      setModelsByProvider(mbp);
      // init drafts
      const d = {};
      for (const r of routing) {
        d[r.task] = {
          primary_provider:   r.primary_provider  || '',
          primary_model:      r.primary_model     || '',
          fallback_provider:  r.fallback_provider || '',
          fallback_model:     r.fallback_model    || '',
          timeout_ms:         r.timeout_ms ?? 30000,
          max_tokens:         r.max_tokens ?? '',
        };
      }
      setDrafts(d);
    } catch (err) {
      toast.error(err.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { reload(); }, []);

  const setField = (task, field, value) => {
    setDrafts(d => ({ ...d, [task]: { ...d[task], [field]: value } }));
  };

  async function onSave(task) {
    setSavingTask(task);
    try {
      const d = drafts[task];
      const body = {
        primary_provider:  d.primary_provider || null,
        primary_model:     d.primary_model.trim(),
        fallback_provider: d.fallback_provider || null,
        fallback_model:    d.fallback_model.trim() || null,
        timeout_ms:        Number(d.timeout_ms) || 30000,
        max_tokens:        d.max_tokens === '' ? null : Number(d.max_tokens),
      };
      // Nếu primary_provider rỗng → backend sẽ reject. Cảnh báo sớm:
      if (!body.primary_provider || !body.primary_model) {
        toast.error('Primary provider + model bắt buộc');
        return;
      }
      await api.adminAiUpdateRouting(task, body);
      toast.success(`Đã lưu routing ${task}`);
      reload();
    } catch (err) {
      const head = err.data?.error || err.message;
      const detail = err.data?.detail ? ` — ${err.data.detail}` : '';
      toast.error(head + detail);
    } finally {
      setSavingTask(null);
    }
  }

  if (loading) return <div className="text-sm text-slate-400">Đang tải routing…</div>;

  return (
    <div className="space-y-3">
      <div className="text-xs text-slate-500">
        Mỗi task AI được định tuyến đến 1 provider chính. Nếu chính fail → tự động thử fallback.
        Chưa validate "provider đã bật" ở client — cấu hình xong hãy kiểm tra lại ở tab Providers.
      </div>

      {/* M44 (2026-04-24): Banner cảnh báo model embedding đã đổi */}
      <RebuildAllEmbeddingBanner />


      {rows.map(r => {
        const t = TASKS.find(x => x.key === r.task);
        const d = drafts[r.task] || {};
        return (
          <div key={r.task} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <div className="flex items-baseline justify-between">
              <div>
                <span className="font-mono text-brand-700 font-medium">{r.task}</span>
                {t && <span className="ml-2 text-sm text-slate-700">{t.label}</span>}
              </div>
              {t && <span className="text-xs text-slate-400">{t.hint}</span>}
            </div>

            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="text-xs font-medium text-slate-700 mb-1">Primary</div>
                <div className="flex gap-2">
                  <select
                    value={d.primary_provider}
                    onChange={e => setField(r.task, 'primary_provider', e.target.value)}
                    className="px-2 py-1.5 rounded border border-slate-300 text-sm bg-white"
                  >
                    <option value="">— chọn —</option>
                    {PROVIDERS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                  </select>
                  <input
                    type="text"
                    placeholder="model ID"
                    list={`models-${r.task}-primary`}
                    value={d.primary_model}
                    onChange={e => setField(r.task, 'primary_model', e.target.value)}
                    className="flex-1 px-2 py-1.5 rounded border border-slate-300 text-sm font-mono"
                  />
                  {/* M19 (2026-04-24) — datalist gợi ý model từ cache provider */}
                  <datalist id={`models-${r.task}-primary`}>
                    {(modelsByProvider[d.primary_provider] || []).map(m => (
                      <option key={m} value={m} />
                    ))}
                  </datalist>
                </div>
                {d.primary_provider && (modelsByProvider[d.primary_provider]?.length > 0) && (
                  <div className="text-[11px] text-slate-400 mt-1">
                    💡 {modelsByProvider[d.primary_provider].length} models gợi ý — gõ chữ để lọc
                  </div>
                )}
                {d.primary_provider && (!modelsByProvider[d.primary_provider] || modelsByProvider[d.primary_provider].length === 0) && (
                  <div className="text-[11px] text-amber-600 mt-1">
                    ⚠ Chưa refresh models — vào tab Providers bấm "🔄 Cập nhật models"
                  </div>
                )}
              </div>
              <div>
                <div className="text-xs font-medium text-slate-700 mb-1">
                  Fallback <span className="text-slate-400">(tuỳ chọn)</span>
                </div>
                <div className="flex gap-2">
                  <select
                    value={d.fallback_provider}
                    onChange={e => setField(r.task, 'fallback_provider', e.target.value)}
                    className="px-2 py-1.5 rounded border border-slate-300 text-sm bg-white"
                  >
                    <option value="">— không —</option>
                    {PROVIDERS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                  </select>
                  <input
                    type="text"
                    placeholder={d.fallback_provider ? 'model ID' : '—'}
                    list={`models-${r.task}-fallback`}
                    disabled={!d.fallback_provider}
                    value={d.fallback_model}
                    onChange={e => setField(r.task, 'fallback_model', e.target.value)}
                    className="flex-1 px-2 py-1.5 rounded border border-slate-300 text-sm font-mono disabled:bg-slate-50"
                  />
                  <datalist id={`models-${r.task}-fallback`}>
                    {(modelsByProvider[d.fallback_provider] || []).map(m => (
                      <option key={m} value={m} />
                    ))}
                  </datalist>
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-slate-700 mb-1">Timeout (ms)</div>
                <input
                  type="number" min="1000" max="600000" step="1000"
                  value={d.timeout_ms}
                  onChange={e => setField(r.task, 'timeout_ms', e.target.value)}
                  className="w-full px-2 py-1.5 rounded border border-slate-300 text-sm"
                />
              </div>
              <div>
                <div className="text-xs font-medium text-slate-700 mb-1">
                  Max tokens <span className="text-slate-400">(rỗng = mặc định)</span>
                </div>
                <input
                  type="number" min="1" max="200000"
                  value={d.max_tokens}
                  onChange={e => setField(r.task, 'max_tokens', e.target.value)}
                  className="w-full px-2 py-1.5 rounded border border-slate-300 text-sm"
                />
              </div>
            </div>

            <div className="mt-3 flex items-center justify-between">
              <span className="text-xs text-slate-400">
                Cập nhật: {r.updated_at ? new Date(r.updated_at).toLocaleString('vi-VN') : '—'}
              </span>
              <button
                onClick={() => onSave(r.task)}
                disabled={savingTask === r.task}
                className="text-xs px-3 py-1.5 rounded bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {savingTask === r.task ? 'Đang lưu…' : 'Lưu'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// M23 (2026-04-24): EmbeddingTab đã BỊ GỠ.
// Auto-embed trigger ở backend khi create/update/import/approve câu hỏi.
// Badge "⚠️ N câu chưa embed" + nút retry nằm ở trang QuestionsPage.
